import React, { useState, useEffect } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import optenicsLogo from "../../assets/optenics_logo.png";
import profilePlaceholder from "../../assets/profile-placeholder.jpg";
import styles from "./style.module.scss";
import { useAppContext } from "../../context/AppContext";
import { useAuth } from "../../context/AuthContext";
import { CaretCircleRightIcon } from "@phosphor-icons/react";
import api from "../../utils/api";

interface HeaderProps {
  isLoggedIn: boolean;
  showModuleName?: boolean;
}

interface OrgRoleType {
  id: number;
  name: string;
}

interface UserType {
  id: number;
  name: string;
}

interface AppAccessLevel {
  id: number;
  name: string;
}

interface App {
  id: string;
  name: string;
}

const Header: React.FC<HeaderProps> = ({ isLoggedIn, showModuleName = true }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const pathname = location.pathname;
  const { editUserName, editOrganizationName } = useAppContext();
  const { user, logout } = useAuth();
  const [showDropdown, setShowDropdown] = useState(false);
  const [profileImage, setProfileImage] = useState<string>(profilePlaceholder);
  const [loading, setLoading] = useState<boolean>(false);
  const [orgRoleTypeName, setOrgRoleTypeName] = useState<string>("");
  const [userTypeName, setUserTypeName] = useState<string>("");
  const [accessLevelName, setAccessLevelName] = useState<string>("");
  const [appName, setAppName] = useState<string>("");

  const toggleDropdown = () => setShowDropdown((prev) => !prev);
  const closeDropdown = () => setShowDropdown(false);

  const handleLogout = async () => {
    try {
      console.log("Attempting logout");
      await logout();
      navigate("/login", { replace: true });
    } catch (err) {
      console.error("Logout failed:", err);
    }
    closeDropdown();
  };

  useEffect(() => {
    const fetchProfilePicture = async () => {
      if (!isLoggedIn || !user?.id) {
        setProfileImage(profilePlaceholder);
        return;
      }

      setLoading(true);
      try {
        const res = await api.post('/core/files/get', {
          user_id: user.id,
          file_for: 'user_profile_picture',
        });
        if (res.data?.files?.length > 0 && res.data.files[0].signed_url) {
          setProfileImage(res.data.files[0].signed_url);
        } else {
          setProfileImage(profilePlaceholder);
        }
      } catch (err) {
        console.error('Header profile picture fetch failed:', err);
        setProfileImage(profilePlaceholder);
      } finally {
        setLoading(false);
      }
    };

    fetchProfilePicture();
  }, [user?.id, isLoggedIn]);

  useEffect(() => {
    const fetchData = async () => {
      if (!id || (!pathname.includes("roles") && !pathname.includes("apps")) || (!pathname.includes("edit") && !pathname.includes("view"))) {
        setOrgRoleTypeName("");
        setUserTypeName("");
        setAccessLevelName("");
        setAppName("");
        return;
      }

      try {
        if (pathname.includes("roles")) {
          if (pathname.includes("org-role")) {
            const res = await api.post<{ roles: OrgRoleType[] }>("/core/role/orgroletype/get", {});
            const roleType = res.data.roles.find((rt) => rt.id === parseInt(id));
            setOrgRoleTypeName(roleType ? roleType.name : `ID ${id}`);
          } else if (pathname.includes("user-type")) {
            const res = await api.post<{ usertypes: UserType[] }>("/core/role/usertype/get", {});
            const userType = res.data.usertypes.find((ut) => ut.id === parseInt(id));
            setUserTypeName(userType ? userType.name : `ID ${id}`);
          } else if (pathname.includes("app-access")) {
            const res = await api.post<{ access_levels: AppAccessLevel[] }>("/core/role/access-level/get", {});
            const accessLevel = res.data.access_levels.find((al) => al.id === parseInt(id));
            setAccessLevelName(accessLevel ? accessLevel.name : `ID ${id}`);
          }
        } else if (pathname.includes("apps")) {
          const res = await api.get<{ apps: App[] }>("/core/app/get");
          const app = res.data.apps.find((a) => a.id === id);
          setAppName(app ? app.name : `ID ${id}`);
        }
      } catch (err) {
        console.error("Failed to fetch data for breadcrumbs:", err);
        setOrgRoleTypeName(`ID ${id}`);
        setUserTypeName(`ID ${id}`);
        setAccessLevelName(`ID ${id}`);
        setAppName(`ID ${id}`);
      }
    };

    fetchData();
  }, [id, pathname]);

  const pathSegments = pathname.split("/").filter(Boolean);
  const isUserEdit = pathSegments[0] === "users" && pathSegments[1] === "edit";
  const isOrgEdit = pathSegments[0] === "organizations" && pathSegments[1] === "edit";
  const isApp = pathSegments[0] === "apps" && pathSegments.length === 1;
  const isAppNew = pathname === "/apps/new";
  const isAppEdit = pathSegments[0] === "apps" && pathSegments[1] === "edit";
  const isAppView = pathSegments[0] === "apps" && pathSegments[1] === "view";
  const isRoleNewOrgRole = pathname === "/roles/new-org-role";
  const isRoleNewUserType = pathname === "/roles/new-user-type";
  const isRoleNewAppAccess = pathname === "/roles/new-app-access";
  const isRoleEditOrgRole = pathSegments[0] === "roles" && pathSegments[1] === "edit-org-role";
  const isRoleEditUserType = pathSegments[0] === "roles" && pathSegments[1] === "edit-user-type";
  const isRoleEditAppAccess = pathSegments[0] === "roles" && pathSegments[1] === "edit-app-access";
  const isRoleViewOrgRole = pathSegments[0] === "roles" && pathSegments[1] === "view-org-role";
  const isRoleViewUserType = pathSegments[0] === "roles" && pathSegments[1] === "view-user-type";
  const isRoleViewAppAccess = pathSegments[0] === "roles" && pathSegments[1] === "view-app-access";
  const isConnections = pathname === "/connections";
  const isNewOrgConnection = pathname === "/connections/new-org-connection";
  const isNewUserConnection = pathname === "/connections/new-user-connection";

  return (
    <header>
      <div className={styles.headerWrapper}>
        <div className={styles.leftContent}>
          {!isLoggedIn && (
            <div className={styles.logo}>
              <img src={optenicsLogo} alt="Optenics" />
            </div>
          )}

          {isLoggedIn && showModuleName && (
            <div className={styles.titleContainer}>
              <div className={styles.titleHolder}>
                {isUserEdit ? (
                  <>
                    <Link to="/users" className={styles.breadcrumbLink}>
                      Users
                    </Link>
                    <span className={styles.titleIconCaret}>
                      <CaretCircleRightIcon size={18} />
                    </span>
                    <span>Edit</span>
                    {editUserName && (
                      <>
                        <span className={styles.titleIconCaret}>
                          <CaretCircleRightIcon size={18} />
                        </span>
                        <span>
                          {editUserName}
                          {editOrganizationName ? ` (${editOrganizationName})` : ""}
                        </span>
                      </>
                    )}
                  </>
                ) : isOrgEdit ? (
                  <>
                    <Link to="/organizations" className={styles.breadcrumbLink}>
                      Organizations
                    </Link>
                    <span className={styles.titleIconCaret}>
                      <CaretCircleRightIcon size={18} />
                    </span>
                    <span>Edit</span>
                    {editOrganizationName && (
                      <>
                        <span className={styles.titleIconCaret}>
                          <CaretCircleRightIcon size={18} />
                        </span>
                        <span>{editOrganizationName}</span>
                      </>
                    )}
                  </>
                ) : isApp ? (
                  <span>Apps</span>
                ) : isAppNew ? (
                  <>
                    <Link to="/apps" className={styles.breadcrumbLink}>
                      Apps
                    </Link>
                    <span className={styles.titleIconCaret}>
                      <CaretCircleRightIcon size={18} />
                    </span>
                    <span>New App</span>
                  </>
                ) : isAppEdit ? (
                  <>
                    <Link to="/apps" className={styles.breadcrumbLink}>
                      Apps
                    </Link>
                    <span className={styles.titleIconCaret}>
                      <CaretCircleRightIcon size={18} />
                    </span>
                    <span>Edit</span>
                    {appName && (
                      <>
                        <span className={styles.titleIconCaret}>
                          <CaretCircleRightIcon size={18} />
                        </span>
                        <span>{appName}</span>
                      </>
                    )}
                  </>
                ) : isAppView ? (
                  <>
                    <Link to="/apps" className={styles.breadcrumbLink}>
                      Apps
                    </Link>
                    <span className={styles.titleIconCaret}>
                      <CaretCircleRightIcon size={18} />
                    </span>
                    <span>View</span>
                    {appName && (
                      <>
                        <span className={styles.titleIconCaret}>
                          <CaretCircleRightIcon size={18} />
                        </span>
                        <span>{appName}</span>
                      </>
                    )}
                  </>
                ) : isRoleNewOrgRole ? (
                  <>
                    <Link to="/roles" className={styles.breadcrumbLink}>
                      Roles
                    </Link>
                    <span className={styles.titleIconCaret}>
                      <CaretCircleRightIcon size={18} />
                    </span>
                    <span>New Organization Role Type</span>
                  </>
                ) : isRoleNewUserType ? (
                  <>
                    <Link to="/roles" className={styles.breadcrumbLink}>
                      Roles
                    </Link>
                    <span className={styles.titleIconCaret}>
                      <CaretCircleRightIcon size={18} />
                    </span>
                    <span>New User Type</span>
                  </>
                ) : isRoleNewAppAccess ? (
                  <>
                    <Link to="/roles" className={styles.breadcrumbLink}>
                      Roles
                    </Link>
                    <span className={styles.titleIconCaret}>
                      <CaretCircleRightIcon size={18} />
                    </span>
                    <span>New App Access Level</span>
                  </>
                ) : isRoleEditOrgRole ? (
                  <>
                    <Link to="/roles" className={styles.breadcrumbLink}>
                      Roles
                    </Link>
                    <span className={styles.titleIconCaret}>
                      <CaretCircleRightIcon size={18} />
                    </span>
                    <span>Edit Organization Role Type</span>
                    {orgRoleTypeName && (
                      <>
                        <span className={styles.titleIconCaret}>
                          <CaretCircleRightIcon size={18} />
                        </span>
                        <span>{orgRoleTypeName}</span>
                      </>
                    )}
                  </>
                ) : isRoleEditUserType ? (
                  <>
                    <Link to="/roles" className={styles.breadcrumbLink}>
                      Roles
                    </Link>
                    <span className={styles.titleIconCaret}>
                      <CaretCircleRightIcon size={18} />
                    </span>
                    <span>Edit User Type</span>
                    {userTypeName && (
                      <>
                        <span className={styles.titleIconCaret}>
                          <CaretCircleRightIcon size={18} />
                        </span>
                        <span>{userTypeName}</span>
                      </>
                    )}
                  </>
                ) : isRoleEditAppAccess ? (
                  <>
                    <Link to="/roles" className={styles.breadcrumbLink}>
                      Roles
                    </Link>
                    <span className={styles.titleIconCaret}>
                      <CaretCircleRightIcon size={18} />
                    </span>
                    <span>Edit App Access Level</span>
                    {accessLevelName && (
                      <>
                        <span className={styles.titleIconCaret}>
                          <CaretCircleRightIcon size={18} />
                        </span>
                        <span>{accessLevelName}</span>
                      </>
                    )}
                  </>
                ) : isRoleViewOrgRole ? (
                  <>
                    <Link to="/roles" className={styles.breadcrumbLink}>
                      Roles
                    </Link>
                    <span className={styles.titleIconCaret}>
                      <CaretCircleRightIcon size={18} />
                    </span>
                    <span>View Organization Role Type</span>
                    {orgRoleTypeName && (
                      <>
                        <span className={styles.titleIconCaret}>
                          <CaretCircleRightIcon size={18} />
                        </span>
                        <span>{orgRoleTypeName}</span>
                      </>
                    )}
                  </>
                ) : isRoleViewUserType ? (
                  <>
                    <Link to="/roles" className={styles.breadcrumbLink}>
                      Roles
                    </Link>
                    <span className={styles.titleIconCaret}>
                      <CaretCircleRightIcon size={18} />
                    </span>
                    <span>View User Type</span>
                    {userTypeName && (
                      <>
                        <span className={styles.titleIconCaret}>
                          <CaretCircleRightIcon size={18} />
                        </span>
                        <span>{userTypeName}</span>
                      </>
                    )}
                  </>
                ) : isRoleViewAppAccess ? (
                  <>
                    <Link to="/roles" className={styles.breadcrumbLink}>
                      Roles
                    </Link>
                    <span className={styles.titleIconCaret}>
                      <CaretCircleRightIcon size={18} />
                    </span>
                    <span>View App Access Level</span>
                    {accessLevelName && (
                      <>
                        <span className={styles.titleIconCaret}>
                          <CaretCircleRightIcon size={18} />
                        </span>
                        <span>{accessLevelName}</span>
                      </>
                    )}
                  </>
                ) : isConnections ? (
                  <>
                    <Link to="/dashboard" className={styles.breadcrumbLink}>
                      Dashboard
                    </Link>
                    <span className={styles.titleIconCaret}>
                      <CaretCircleRightIcon size={18} />
                    </span>
                    <span>Connections</span>
                  </>
                ) : isNewOrgConnection ? (
                  <>
                    <Link to="/dashboard" className={styles.breadcrumbLink}>
                      Dashboard
                    </Link>
                    <span className={styles.titleIconCaret}>
                      <CaretCircleRightIcon size={18} />
                    </span>
                    <Link to="/connections" className={styles.breadcrumbLink}>
                      Connections
                    </Link>
                    <span className={styles.titleIconCaret}>
                      <CaretCircleRightIcon size={18} />
                    </span>
                    <span>New Organization Connection</span>
                  </>
                ) : isNewUserConnection ? (
                  <>
                    <Link to="/dashboard" className={styles.breadcrumbLink}>
                      Dashboard
                    </Link>
                    <span className={styles.titleIconCaret}>
                      <CaretCircleRightIcon size={18} />
                    </span>
                    <Link to="/connections" className={styles.breadcrumbLink}>
                      Connections
                    </Link>
                    <span className={styles.titleIconCaret}>
                      <CaretCircleRightIcon size={18} />
                    </span>
                    <span>New User Connection</span>
                  </>
                ) : (
                  <span>{pathSegments[0]?.charAt(0).toUpperCase() + pathSegments[0]?.slice(1)}</span>
                )}
              </div>
            </div>
          )}
        </div>

        {isLoggedIn && (
          <div className={styles.rightNav}>
            <nav className={styles.navIcons}>
              <div className={styles.profileWrapper}>
                <button
                  className={styles.profileButton}
                  onClick={toggleDropdown}
                >
                  <img
                    src={profileImage}
                    alt="User Profile"
                    className={`${styles.profileImage} ${loading ? styles.loading : ''}`}
                  />
                </button>

                {showDropdown && (
                  <ul className={styles.dropdownMenu}>
                    <li>
                      <Link
                        to="/profile"
                        className={styles.dropdownItem}
                        onClick={closeDropdown}
                      >
                        Profile
                      </Link>
                    </li>
                    <li>
                      <button
                        className={styles.dropdownItem}
                        onClick={handleLogout}
                      >
                        Logout
                      </button>
                    </li>
                  </ul>
                )}
              </div>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
